import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Form, InputGroup, Modal } from "react-bootstrap";
import QuickQuizService, { TestResultsData, ResultStudent } from "../../_services/QuickQuizService";
import myAxios from "../../_services/axiositeachWrapper";
import Config from "../../_config/config";
import ApiService from "../../_services/ApiService";
import { useITeachConfig } from "../../_context/PresetContext";
import { useAccessControl } from "../../access-control/AccessControlContext";

/**
 * Configurable result columns. Static columns (Student Name, Code, Marks) are
 * always shown and are NOT in this list. Every column below is hidden by
 * default and only appears when its key is enabled in
 * customer_detail_master.JsonConfig.test.testViewResult (legacy fallback:
 * testAttribute.testViewResult). Each value may be a bare boolean or an
 * `{ enabled, label }` object; `label` overrides the built-in display name here.
 *
 * `sectionMarks` is a single toggle that controls all per-subject/section columns.
 */
const CONFIGURABLE_COLUMNS: { key: string; label: string }[] = [
  { key: "batchName", label: "Batch Name" },
  { key: "username", label: "Username" },
  { key: "password", label: "Password" },
  { key: "year", label: "Year" },
  { key: "courseName", label: "Course Name" },
  { key: "correct", label: "Correct" },
  { key: "wrong", label: "Wrong" },
  { key: "unattempted", label: "Unattempted" },
  { key: "sectionMarks", label: "Subject / Section Marks" },
  { key: "maxMarks", label: "Max Marks" },
  { key: "percentage", label: "Percentage (%)" },
  { key: "testStatus", label: "Test Status" },
  { key: "testRank", label: "Test Rank" },
  { key: "schoolRank", label: "Centre Rank" },
  { key: "batchRank", label: "Batch Rank" },
  { key: "grade", label: "Grade" },
  { key: "submittedAt", label: "Submitted At" },
  { key: "audit", label: "Audit" },
  { key: "delete", label: "Delete" },
];

/** Built-in fallback display names, keyed by column key. */
const DEFAULT_COL_LABELS: Record<string, string> = CONFIGURABLE_COLUMNS.reduce(
  (acc, c) => { acc[c.key] = c.label; return acc; },
  {} as Record<string, string>,
);

/**
 * A column's config value may be a bare boolean (legacy) or an
 * `{ enabled, label }` object. This returns whether the column is enabled.
 */
const isColEnabled = (v: any): boolean =>
  v === true || (v != null && typeof v === "object" && v.enabled === true);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${mon}-${year} ${hours}:${mins}`;
};

interface TestResultsPanelProps {
  testId: number;
  onBack: () => void;
  onViewTest?: (testId: number) => void;
}

const StatCard: React.FC<{ label: string; value: number | string; color: string }> = ({ label, value, color }) => (
  <div
    style={{
      flex: "1 1 120px",
      background: color,
      borderRadius: "8px",
      padding: "12px 16px",
      color: "var(--text-on-brand)",
      textAlign: "center",
      minWidth: "120px",
      margin: "2px",
    }}
  >
    <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{value}</div>
    <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>{label}</div>
  </div>
);

const TestResultsPanel: React.FC<TestResultsPanelProps> = ({ testId, onBack, onViewTest }) => {
  const [data, setData] = useState<TestResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [batchFilter, setBatchFilter] = useState<string>("");
  const [sortField, setSortField] = useState<string>("rank");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [cloudUploading, setCloudUploading] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);

  // Audit + Question Analysis modals (Java: showAuditTrailDetails + viewMyPerformance)
  const [auditModal, setAuditModal] = useState<{ show: boolean; student: ResultStudent | null; data: any }>({ show: false, student: null, data: null });
  const [qaModal, setQaModal] = useState<{ show: boolean; student: ResultStudent | null; data: any }>({ show: false, student: null, data: null });
  const [modalLoading, setModalLoading] = useState(false);
  const [questionsById, setQuestionsById] = useState<Record<number, any>>({});
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);

  // Column visibility + display-name config
  // (customer_detail_master.JsonConfig.test.testViewResult; legacy: testAttribute.testViewResult)
  const { config: iTeachConfig } = useITeachConfig();
  const [colCfg, setColCfg] = useState<Record<string, any>>({});
  const [showColsModal, setShowColsModal] = useState(false);
  const [colsDraft, setColsDraft] = useState<Record<string, boolean>>({});
  const [savingCols, setSavingCols] = useState(false);

  // Sync local column config whenever the tenant config loads/changes.
  useEffect(() => {
    setColCfg(
      iTeachConfig?.test?.testViewResult ||
        iTeachConfig?.testAttribute?.testViewResult ||
        {},
    );
  }, [iTeachConfig]);

  // A configurable column is shown only when explicitly enabled.
  const vis = (key: string): boolean => isColEnabled(colCfg[key]);

  // Display name: prefer the label stored in the tenant JSON, else the built-in default.
  const colLabel = (key: string): string => {
    const v = colCfg[key];
    if (v != null && typeof v === "object" && typeof v.label === "string" && v.label.trim()) {
      return v.label;
    }
    return DEFAULT_COL_LABELS[key] ?? key;
  };

  // Who may open the "Columns" configuration modal. Root/admin can ALWAYS open it;
  // additionally, any role listed in the reserved `configRoleIds` meta key inside
  // test.testViewResult is allowed. Empty/absent list ⇒ root/admin only.
  const { isAdmin, userRoleIds } = useAccessControl();
  const colsConfigRoleIds: number[] = Array.isArray(colCfg["configRoleIds"])
    ? colCfg["configRoleIds"]
    : [];
  const canConfigCols = isAdmin || colsConfigRoleIds.some((r) => userRoleIds.includes(r));

  const openColsModal = () => {
    const draft: Record<string, boolean> = {};
    for (const c of CONFIGURABLE_COLUMNS) draft[c.key] = isColEnabled(colCfg[c.key]);
    setColsDraft(draft);
    setShowColsModal(true);
  };

  const saveColsConfig = async () => {
    setSavingCols(true);
    try {
      // Persist the full set as { enabled, label } so the display name lives in
      // the tenant JSON alongside the on/off state. Unchecked columns are kept
      // (enabled:false) so they stay hidden.
      const full: Record<string, any> = {};
      // Preserve the reserved access meta key (roles allowed to open this modal)
      // so saving column toggles doesn't wipe the role configuration.
      if (Array.isArray(colCfg["configRoleIds"])) full["configRoleIds"] = colCfg["configRoleIds"];
      for (const c of CONFIGURABLE_COLUMNS) {
        full[c.key] = { enabled: colsDraft[c.key] === true, label: colLabel(c.key) };
      }
      await ApiService.saveTestViewResultConfig(full);
      setColCfg(full);
      setShowColsModal(false);
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to save column configuration");
    } finally {
      setSavingCols(false);
    }
  };

  const handleShowAudit = async (student: ResultStudent) => {
    setModalLoading(true);
    setAuditModal({ show: true, student, data: null });
    try {
      const result = await QuickQuizService.getStudentAuditTrail(testId, student.enquiryId);
      setAuditModal({ show: true, student, data: result });
    } catch {
      setAuditModal({ show: true, student, data: { error: "Failed to load audit data" } });
    } finally {
      setModalLoading(false);
    }
  };

  const handleShowQuestionAnalysis = async (student: ResultStudent) => {
    setModalLoading(true);
    setExpandedRowKey(null);
    setQaModal({ show: true, student, data: null });
    try {
      // Fetch student result and test question paper in parallel so expansion is instant.
      const [result, qp] = await Promise.all([
        QuickQuizService.getStudentTestResult(testId, student.enquiryId),
        Object.keys(questionsById).length === 0
          ? QuickQuizService.getTestQuestions(testId).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (qp?.data?.questions && Array.isArray(qp.data.questions)) {
        const map: Record<number, any> = {};
        for (const q of qp.data.questions) {
          if (q.questionId != null) map[q.questionId] = q;
        }
        setQuestionsById(map);
      }
      setQaModal({ show: true, student, data: result });
    } catch {
      setQaModal({ show: true, student, data: { error: "Failed to load question analysis" } });
    } finally {
      setModalLoading(false);
    }
  };

  const loadResults = () => {
    setLoading(true);
    QuickQuizService.getTestResults(testId)
      .then(setData)
      .catch((err: any) => setError(err?.response?.data?.message || err.message || "Failed to load results"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadResults();
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner role="status" animation="border" />
        <p className="text-muted mt-2">Loading results...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <Card.Body className="text-center py-4">
          <p className="text-danger">{error || "Failed to load results"}</p>
          <Button variant="secondary" size="sm" onClick={onBack} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Back</Button>
        </Card.Body>
      </Card>
    );
  }

  const { test, sections, stats, students } = data;
  const hasQuestions = (test?.totalQuestions ?? 0) > 0;

  // Count of currently-visible columns (3 static: Name, Code, Marks + enabled
  // configurable columns). Used for the empty-state row colSpan.
  const visibleColCount = (() => {
    let n = 3; // Student Name, Code, Marks (always shown)
    for (const c of CONFIGURABLE_COLUMNS) {
      if (c.key === "sectionMarks") {
        if (vis("sectionMarks")) n += sections.length;
      } else if (c.key === "correct" || c.key === "wrong" || c.key === "unattempted") {
        if (hasQuestions && vis(c.key)) n += 1;
      } else if (vis(c.key)) {
        n += 1;
      }
    }
    return n;
  })();

  // Build unique batch list from students (for the batch filter dropdown)
  const batchOptions = (() => {
    const map = new Map<string, { batchId: number; batchCode: string; batchName: string; count: number }>();
    for (const s of students) {
      if (!s.batchId) continue;
      const key = String(s.batchId);
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, {
          batchId: s.batchId,
          batchCode: s.batchCode || "",
          batchName: s.batchName || "",
          count: 1,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.batchCode || a.batchName).localeCompare(b.batchCode || b.batchName),
    );
  })();

  // Filter
  let filtered: ResultStudent[] = students;
  if (batchFilter) {
    filtered = filtered.filter((s) => String(s.batchId || "") === batchFilter);
  }
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (s) =>
        s.studentName.toLowerCase().includes(q) ||
        s.studentCode.toLowerCase().includes(q) ||
        (s.batchCode || "").toLowerCase().includes(q) ||
        (s.batchName || "").toLowerCase().includes(q) ||
        String(s.enquiryId).includes(q)
    );
  }

  // Sort
  filtered = [...filtered].sort((a, b) => {
    let aVal: any, bVal: any;
    switch (sortField) {
      case "name": aVal = a.studentName; bVal = b.studentName; break;
      case "marks": aVal = a.totalMarks || 0; bVal = b.totalMarks || 0; break;
      case "percentage": aVal = a.percentage; bVal = b.percentage; break;
      case "rank": aVal = a.rank || 9999; bVal = b.rank || 9999; break;
      default: aVal = a.rank || 9999; bVal = b.rank || 9999;
    }
    if (typeof aVal === "string") {
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === "asc" ? aVal - bVal : bVal - aVal;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortIcon = (field: string) => {
    if (sortField !== field) return " \u2195";
    return sortDir === "asc" ? " \u2191" : " \u2193";
  };

  // CSV export
  const exportCSV = () => {
    const headers = [
      "Rank", "Student Name", "Code", "Batch Name", "Username", "Password", "Year", "Course Name",
      "Marks", "Total", "Percentage", "Test Status",
      "Correct", "Wrong", "Unattempted",
      ...sections.map((s) => s.name),
      "Submitted At"
    ];
    const rows = filtered.map((s) => [
      s.rank || "-",
      s.studentName,
      s.studentCode,
      s.batchName || "-",
      s.username || "-",
      s.password || "-",
      s.year || "-",
      s.courseName || "-",
      s.totalMarks ?? "-",
      s.maxMarks,
      `${s.percentage}%`,
      s.testStatus || "-",
      s.correct,
      s.wrong,
      s.unattempted,
      ...sections.map((sec) => s.sectionMarks[sec.sectionId] ?? "-"),
      s.entryDate || "-"
    ]);

    const csv = [headers.join(","), ...rows.map((r) => r.map((v) => `"${v}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `results_test_${testId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Excel export (server-side .xlsx)
  const exportExcel = async () => {
    setExcelDownloading(true);
    try {
      const url = `${Config.nodeSiteUrl}v1/quick-quiz/${testId}/results/export-excel`;
      const response = await myAxios.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `results_test_${testId}.xlsx`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to export Excel");
    } finally {
      setExcelDownloading(false);
    }
  };

  // Upload to cloud (S3)
  const uploadToCloud = async () => {
    setCloudUploading(true);
    try {
      const result = await QuickQuizService.uploadResultsToCloud(testId);
      alert(result.message || "Result uploaded successfully");
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to upload to cloud");
    } finally {
      setCloudUploading(false);
    }
  };

  // Delete student RDS marks
  const handleDeleteMarks = async (enquiryId: number, studentName: string) => {
    if (!window.confirm(`This process is irreversible.\nDo you still want to delete marks for ${studentName}?`)) return;
    setDeletingId(enquiryId);
    try {
      await QuickQuizService.deleteStudentMarks(testId, enquiryId);
      alert("Marks deleted successfully");
      loadResults();
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to delete marks");
    } finally {
      setDeletingId(null);
    }
  };

  // Delete student DynamoDB responses
  const handleDeleteDynamoMarks = async (enquiryId: number, studentName: string) => {
    if (!window.confirm(`This process is irreversible.\nDo you still want to delete DynamoDB marks for ${studentName}?`)) return;
    setDeletingId(enquiryId);
    try {
      await QuickQuizService.deleteStudentDynamoResponses(testId, enquiryId);
      alert("DynamoDB marks deleted successfully");
      loadResults();
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to delete DynamoDB marks");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* Header */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <strong>Test Results</strong>
            <span style={{ margin: "0 8px", color: "var(--text-muted)" }}>
              {test.name} (ID: {test.testId})
            </span>
          </span>
          <div className="d-flex gap-2">
            {canConfigCols && (
              <Button variant="secondary" size="sm" onClick={openColsModal} title="Configure visible columns (tenant-wide)" style={{ backgroundColor: "#6f42c1", color: "var(--text-on-brand)", border: "1px solid #6f42c1", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}>
                &#9881; Columns
              </Button>
            )}
            {onViewTest && (
              <Button variant="outline-primary" size="sm" onClick={() => onViewTest(testId)} style={{ backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}>
                View Test
              </Button>
            )}
            <Button variant="outline-success" size="sm" onClick={exportCSV} style={{ backgroundColor: "var(--surface-card)", color: "var(--status-success)", border: "1px solid var(--status-success)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}>
              Export CSV
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={exportExcel}
              disabled={excelDownloading}
              title="Export to Excel"
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}
            >
              {excelDownloading ? <Spinner role="status" animation="border" size="sm" /> : <span>&#128196; Excel</span>}
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={uploadToCloud}
              disabled={cloudUploading}
              title="Export to Cloud"
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}
            >
              {cloudUploading ? <Spinner role="status" animation="border" size="sm" /> : <span>&#9729; Cloud</span>}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={onBack} style={{ backgroundColor: "var(--surface-card)", color: "var(--text-muted)", border: "1px solid #6c757d", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}>
              Back
            </Button>
          </div>
        </Card.Header>
      </Card>

      {/* Stats Cards */}
      <div className="d-flex flex-wrap mb-3">
        <StatCard label="Total Students" value={stats.totalStudents} color="var(--text-muted)" />
        <StatCard label="Submitted" value={stats.submitted} color="var(--status-success)" />
        <StatCard label="Average Marks" value={`${stats.averageMarks} / ${test.totalMarks}`} color="var(--status-info)" />
        <StatCard label="Average %" value={`${stats.averagePercentage}%`} color="var(--brand-primary)" />
        <StatCard label="Highest" value={stats.highestMarks} color="var(--status-success)" />
        <StatCard label="Lowest" value={stats.lowestMarks} color="var(--status-danger)" />
        <StatCard label="Pass" value={stats.passCount} color="var(--status-success)" />
        <StatCard label="Fail" value={stats.failCount} color="var(--status-danger)" />
      </div>

      {/* Search */}
      <Card className="mb-3">
        <Card.Body style={{ padding: "10px 16px" }}>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: "6px" }}>
            <InputGroup style={{ maxWidth: "260px", margin: "2px", height: "31px" }}>
              <Form.Control
                size="sm"
                placeholder="Search by name, code, batch, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ height: "31px", fontSize: "0.8rem" }}
              />
            </InputGroup>
            {batchOptions.length > 0 && (
              <Form.Select
                size="sm"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                style={{
                  maxWidth: "240px",
                  height: "31px",
                  padding: "2px 24px 2px 8px",
                  margin: "2px",
                  fontSize: "0.8rem",
                  lineHeight: "1.4",
                  borderRadius: "4px",
                  border: "1px solid var(--border-strong)",
                }}
                title="Filter students by batch"
              >
                <option value="">All Batches ({batchOptions.length})</option>
                {batchOptions.map((b) => {
                  const label = b.batchCode
                    ? `${b.batchCode}${b.batchName ? ` — ${b.batchName}` : ""}`
                    : b.batchName || `Batch #${b.batchId}`;
                  return (
                    <option key={b.batchId} value={String(b.batchId)}>
                      {label} ({b.count})
                    </option>
                  );
                })}
              </Form.Select>
            )}
            <span className="text-muted" style={{ fontSize: "0.8rem", margin: "2px 8px" }}>
              Showing {filtered.length} of {students.length} students
            </span>
            {(batchFilter || searchQuery) && (
              <button
                type="button"
                onClick={() => { setBatchFilter(""); setSearchQuery(""); }}
                style={{
                  backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none",
                  padding: "4px 12px", fontSize: "0.75rem", borderRadius: "4px",
                  margin: "2px", cursor: "pointer",
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Results Table */}
      <Card>
        <Card.Body style={{ padding: "0" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: "0.8rem" }}>
              <thead className="table-light">
                <tr>
                  <th
                    style={{ cursor: "pointer", whiteSpace: "nowrap", paddingLeft: "12px" }}
                    onClick={() => handleSort("name")}
                  >
                    Student Name{sortIcon("name")}
                  </th>
                  <th>Code</th>
                  {vis("batchName") && <th style={{ whiteSpace: "nowrap" }}>{colLabel("batchName")}</th>}
                  {vis("username") && <th style={{ whiteSpace: "nowrap" }}>{colLabel("username")}</th>}
                  {vis("password") && <th style={{ whiteSpace: "nowrap" }}>{colLabel("password")}</th>}
                  {vis("year") && <th style={{ whiteSpace: "nowrap" }}>{colLabel("year")}</th>}
                  {vis("courseName") && <th style={{ whiteSpace: "nowrap" }}>{colLabel("courseName")}</th>}
                  {hasQuestions && vis("correct") && <th className="text-center">{colLabel("correct")}</th>}
                  {hasQuestions && vis("wrong") && <th className="text-center">{colLabel("wrong")}</th>}
                  {hasQuestions && vis("unattempted") && <th className="text-center">{colLabel("unattempted")}</th>}
                  {vis("sectionMarks") && sections.map((sec) => (
                    <th key={sec.sectionId} className="text-center" style={{ whiteSpace: "nowrap" }}>
                      {sec.name}
                      <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 400 }}>
                        ({sec.totalMarks})
                      </div>
                    </th>
                  ))}
                  <th
                    className="text-center"
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => handleSort("marks")}
                  >
                    Marks{sortIcon("marks")}
                  </th>
                  {vis("maxMarks") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("maxMarks")}</th>}
                  {vis("percentage") && (
                    <th
                      className="text-center"
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                      onClick={() => handleSort("percentage")}
                    >
                      %{sortIcon("percentage")}
                    </th>
                  )}
                  {vis("testStatus") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("testStatus")}</th>}
                  {vis("testRank") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("testRank")}</th>}
                  {vis("schoolRank") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("schoolRank")}</th>}
                  {vis("batchRank") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("batchRank")}</th>}
                  {vis("grade") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("grade")}</th>}
                  {vis("submittedAt") && <th style={{ whiteSpace: "nowrap" }}>{colLabel("submittedAt")}</th>}
                  {vis("audit") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("audit")}</th>}
                  {vis("delete") && <th className="text-center" style={{ whiteSpace: "nowrap" }}>{colLabel("delete")}</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColCount}
                      className="text-center text-muted py-4"
                    >
                      {students.length === 0
                        ? "No results found for this test."
                        : "No students match the search."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => {
                    const pctColor =
                      s.percentage >= 80 ? "#28a745" :
                      s.percentage >= 60 ? "#17a2b8" :
                      s.percentage >= 40 ? "#ffc107" : "#dc3545";
                    const isDeleting = deletingId === s.enquiryId;

                    return (
                      <tr key={s.enquiryId}>
                        <td style={{ paddingLeft: "12px" }}><strong>{s.studentName}</strong></td>
                        <td>
                          <div>{s.studentCode || "-"}</div>
                          {(s.batchCode || s.batchName) && (
                            <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", marginTop: "1px" }}>
                              {s.batchCode || s.batchName}
                              {s.batchCode && s.batchName ? ` · ${s.batchName}` : ""}
                            </div>
                          )}
                        </td>
                        {vis("batchName") && <td style={{ whiteSpace: "nowrap" }}>{s.batchName || "-"}</td>}
                        {vis("username") && <td style={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>{s.username || "-"}</td>}
                        {vis("password") && <td style={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>{s.password || "-"}</td>}
                        {vis("year") && <td style={{ whiteSpace: "nowrap" }}>{s.year || "-"}</td>}
                        {vis("courseName") && (
                          <td style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.courseName || ""}>
                            {s.courseName || "-"}
                          </td>
                        )}
                        {hasQuestions && vis("correct") && <td className="text-center" style={{ color: "var(--status-success)" }}>{s.correct}</td>}
                        {hasQuestions && vis("wrong") && <td className="text-center" style={{ color: "var(--status-danger)" }}>{s.wrong}</td>}
                        {hasQuestions && vis("unattempted") && <td className="text-center" style={{ color: "var(--text-muted)" }}>{s.unattempted}</td>}
                        {vis("sectionMarks") && sections.map((sec) => (
                          <td key={sec.sectionId} className="text-center">
                            {s.sectionMarks[sec.sectionId] != null
                              ? s.sectionMarks[sec.sectionId]
                              : "-"}
                          </td>
                        ))}
                        <td className="text-center">
                          <strong>{s.totalMarks ?? "-"}</strong>
                        </td>
                        {vis("maxMarks") && <td className="text-center" style={{ color: "var(--text-muted)" }}>{s.maxMarks ?? "-"}</td>}
                        {vis("percentage") && (
                          <td className="text-center">
                            <span style={{ color: pctColor, fontWeight: 600 }}>
                              {s.percentage}%
                            </span>
                          </td>
                        )}
                        {vis("testStatus") && (
                          <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                            {(() => {
                              const ts = s.testStatus;
                              const style: React.CSSProperties = {
                                display: "inline-block",
                                padding: "2px 10px",
                                borderRadius: "999px",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                letterSpacing: "0.3px",
                                border: "1px solid",
                              };
                              if (ts === "PRESENT") {
                                return <span style={{ ...style, background: "var(--status-success-bg)", color: "var(--status-success-text)", borderColor: "#86efac" }}>PRESENT</span>;
                              }
                              if (ts === "ABSENT") {
                                return <span style={{ ...style, background: "var(--status-danger-bg)", color: "var(--status-danger)", borderColor: "var(--status-danger-border)" }}>ABSENT</span>;
                              }
                              if (ts === "DNA") {
                                return <span style={{ ...style, background: "var(--surface-sunken)", color: "var(--text-body)", borderColor: "var(--border-strong)" }}>DNA</span>;
                              }
                              return <span style={{ color: "var(--text-faint)" }}>-</span>;
                            })()}
                          </td>
                        )}
                        {vis("testRank") && (
                          <td className="text-center" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                            {s.testRank ?? "-"}
                          </td>
                        )}
                        {vis("schoolRank") && (
                          <td className="text-center" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                            {s.schoolRank ?? "-"}
                          </td>
                        )}
                        {vis("batchRank") && (
                          <td className="text-center" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                            {s.batchRank ?? "-"}
                          </td>
                        )}
                        {vis("grade") && (
                          <td className="text-center">
                            {s.studentGrade ? (
                              <span style={{
                                display: "inline-block",
                                padding: "2px 10px",
                                borderRadius: "999px",
                                background: "var(--brand-primary-subtle)",
                                color: "var(--brand-primary-hover)",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                border: "1px solid var(--brand-primary-subtle)",
                              }}>
                                {s.studentGrade}
                              </span>
                            ) : (
                              <span style={{ color: "var(--text-faint)" }}>-</span>
                            )}
                          </td>
                        )}
                        {vis("submittedAt") && <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(s.entryDate)}</td>}
                        {vis("audit") && (
                          <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                            <span
                              title="Audit Trail"
                              onClick={() => handleShowAudit(s)}
                              style={{ color: "var(--brand-primary)", cursor: "pointer", fontSize: "1.1rem", fontWeight: 700, margin: "0 6px" }}
                            >
                              &#9432;
                            </span>
                            <span
                              title="Student Question Analysis"
                              onClick={() => handleShowQuestionAnalysis(s)}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "22px",
                                height: "22px",
                                borderRadius: "50%",
                                background: "var(--brand-primary-hover)",
                                color: "var(--text-on-brand)",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                verticalAlign: "middle",
                              }}
                            >
                              Q
                            </span>
                          </td>
                        )}
                        {vis("delete") && (
                          <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                            {isDeleting ? (
                              <Spinner role="status" animation="border" size="sm" />
                            ) : (
                              <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                                <button
                                  type="button"
                                  title="Delete RDS Marks — removes all aggregated marks and section results from the database (irreversible)"
                                  aria-label="Delete RDS Marks"
                                  onClick={() => handleDeleteMarks(s.enquiryId, s.studentName)}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                    padding: "3px 8px", borderRadius: 4, border: "1px solid var(--status-danger)",
                                    background: "var(--surface-card)", color: "var(--status-danger)", cursor: "pointer",
                                    fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
                                  }}
                                >
                                  🗑 RDS
                                </button>
                                <button
                                  type="button"
                                  title="Delete DynamoDB Responses — removes raw question-level attempts from DynamoDB (irreversible). Use before dyna2rds re-sync."
                                  aria-label="Delete DynamoDB Responses"
                                  onClick={() => handleDeleteDynamoMarks(s.enquiryId, s.studentName)}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                    padding: "3px 8px", borderRadius: 4, border: "1px solid #fd7e14",
                                    background: "var(--surface-card)", color: "#fd7e14", cursor: "pointer",
                                    fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
                                  }}
                                >
                                  🗑 DynamoDB
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* Audit Trail Modal (Java: showAuditTrailDetails) */}
      <Modal
        show={auditModal.show}
        onHide={() => setAuditModal({ show: false, student: null, data: null })}
        size="lg"
        centered
        dialogClassName="audit-modal-dialog"
      >
        <style>{`
          .audit-modal-dialog .modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
          .audit-modal-dialog .modal-header { background: linear-gradient(135deg, #1e293b 0%, #334155 100%); border-bottom: none; padding: 16px 22px; }
          .audit-modal-dialog .modal-header .btn-close { filter: invert(1) brightness(2); opacity: 0.85; }
          .audit-modal-dialog .modal-body { background: #f8fafc; padding: 20px 22px; max-height: 75vh; overflow-y: auto; }
          .audit-modal-dialog .modal-footer { background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 12px 22px; }
          .audit-chip { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; font-size:0.72rem; font-weight:600; }
        `}</style>
        <Modal.Header closeButton style={{ color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "0.98rem", fontWeight: 600, color: "var(--text-on-brand)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: "var(--gradient-brand)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {auditModal.student?.studentName?.[0]?.toUpperCase() || "A"}
              </span>
              <div style={{ lineHeight: 1.2 }}>
                <div>{auditModal.student?.studentName || ""}</div>
                <div style={{ fontSize: "0.72rem", opacity: 0.75, fontWeight: 400 }}>
                  {data?.test?.name || ""} · Test #{testId} · Audit Trail
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="text-center py-4"><Spinner role="status" animation="border" size="sm" /> <span style={{ marginLeft: "8px", color: "var(--text-muted)" }}>Loading audit trail…</span></div>
          ) : auditModal.data?.error ? (
            <div style={{ padding: "12px 16px", background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: "10px", color: "var(--status-danger)" }}>
              {auditModal.data.error}
            </div>
          ) : auditModal.data ? (
            <>
              {/* Device Info Card */}
              <div
                style={{
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "12px",
                  padding: "14px 18px",
                  marginBottom: "14px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                  Device & Connection
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  <span className="audit-chip" style={{ background: "var(--brand-primary-subtle)", color: "var(--brand-primary-hover)", border: "1px solid var(--brand-primary-subtle)" }}>
                    <span style={{ fontSize: "0.85rem" }}>&#128187;</span> {auditModal.data.deviceInfo?.os || "Unknown OS"}
                  </span>
                  <span className="audit-chip" style={{ background: "#ecfeff", color: "#0e7490", border: "1px solid #a5f3fc" }}>
                    <span style={{ fontSize: "0.85rem" }}>&#127760;</span> {auditModal.data.deviceInfo?.browser || "Unknown browser"}
                  </span>
                  <span className="audit-chip" style={{ background: "var(--status-warning-bg)", color: "var(--status-warning-text)", border: "1px solid var(--status-warning-border)" }}>
                    <span style={{ fontSize: "0.85rem" }}>&#128225;</span> {auditModal.data.deviceInfo?.ip || "No IP"}
                  </span>
                </div>
              </div>

              {/* Verdict banner — one clear answer: student submitted vs staff populated */}
              {auditModal.data.submitVerdict && (() => {
                const v = auditModal.data.submitVerdict;
                const palette = v.type === "student"
                  ? { bg: "var(--status-success-bg)", text: "var(--status-success-text)", border: "var(--status-success-border)", accent: "var(--status-success)", icon: "✔", tag: "STUDENT SUBMITTED" }
                  : v.type === "staff"
                    ? { bg: "var(--status-warning-bg)", text: "var(--status-warning-text)", border: "var(--status-warning-border)", accent: "var(--status-warning)", icon: "⚠", tag: "STAFF POPULATED" }
                    : { bg: "var(--surface-muted)", text: "var(--text-muted)", border: "var(--border-default)", accent: "var(--text-faint)", icon: "—", tag: "NO SUBMISSION" };
                return (
                  <div style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderLeft: `4px solid ${palette.accent}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "14px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <span style={{ fontSize: "1.15rem", lineHeight: 1.2, color: palette.accent, flexShrink: 0 }}>{palette.icon}</span>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: "4px", background: palette.accent, color: "var(--text-on-brand, #fff)", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.4px" }}>{palette.tag}</span>
                        <span style={{ fontWeight: 700, fontSize: "0.9rem", color: palette.text }}>{v.title}</span>
                      </div>
                      <div style={{ fontSize: "0.82rem", color: palette.text, lineHeight: 1.5 }}>{v.detail}</div>
                    </div>
                  </div>
                );
              })()}

              {/* Submit Status Cards — supporting technical detail behind the verdict */}
              {(auditModal.data.dynamoSubmitStatus || auditModal.data.rdsSubmitStatus) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px", marginBottom: "14px" }}>
                  {auditModal.data.dynamoSubmitStatus && (
                    <div
                      style={{
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderLeft: "4px solid var(--status-warning)",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        fontSize: "0.82rem",
                        color: "var(--text-strong)",
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          background: "var(--status-warning-bg)",
                          color: "var(--status-warning-text)",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                          flexShrink: 0,
                        }}
                      >
                        DYNAMO DB
                      </span>
                      <span style={{ lineHeight: 1.5 }}>{auditModal.data.dynamoSubmitStatus}</span>
                    </div>
                  )}
                  {auditModal.data.rdsSubmitStatus && (
                    <div
                      style={{
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderLeft: "4px solid #0891b2",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        fontSize: "0.82rem",
                        color: "var(--text-strong)",
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          background: "#cffafe",
                          color: "#0e7490",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                          flexShrink: 0,
                        }}
                      >
                        RDS
                      </span>
                      <span style={{ lineHeight: 1.5 }}>{auditModal.data.rdsSubmitStatus}</span>
                    </div>
                  )}
                </div>
              )}

              {/* User Actions Timeline */}
              <div
                style={{
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "12px",
                  padding: "14px 18px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>User Action Timeline</span>
                  {auditModal.data.userActions?.length > 0 && (
                    <span style={{ fontSize: "0.7rem", color: "var(--text-faint)", fontWeight: 600 }}>
                      {auditModal.data.userActions.length} event{auditModal.data.userActions.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                {auditModal.data.userActions?.length > 0 ? (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {auditModal.data.userActions.map((a: any, i: number) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          padding: "8px 0",
                          borderBottom: i < auditModal.data.userActions.length - 1 ? "1px solid #f1f5f9" : "none",
                          fontSize: "0.82rem",
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "var(--brand-primary)",
                            marginTop: "6px",
                            flexShrink: 0,
                            boxShadow: "0 0 0 3px rgba(99,102,241,0.15)",
                          }}
                        />
                        <div style={{ flex: 1, display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
                          <span style={{ color: "var(--text-strong)", fontWeight: 500 }}>{a.action}</span>
                          <span style={{ color: "var(--text-muted)", fontSize: "0.75rem", fontFamily: "monospace", whiteSpace: "nowrap" }}>{a.timestamp}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-faint)", fontSize: "0.85rem" }}>
                    No user actions recorded.
                  </div>
                )}
              </div>

              {/* History (RDS) */}
              {auditModal.data.historyEntries?.length > 0 && (
                <div
                  style={{
                    background: "var(--surface-card)",
                    border: "1px solid var(--border-default)",
                    borderRadius: "12px",
                    padding: "14px 18px",
                    marginTop: "14px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                    RDS History
                  </div>
                  <pre style={{ fontSize: "0.75rem", background: "var(--surface-page)", padding: "10px 12px", borderRadius: "8px", border: "1px solid var(--border-default)", maxHeight: "200px", overflow: "auto", margin: 0, color: "var(--text-strong)" }}>
                    {JSON.stringify(auditModal.data.historyEntries, null, 2)}
                  </pre>
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            onClick={() => setAuditModal({ show: false, student: null, data: null })}
            style={{ backgroundColor: "#475569", color: "var(--text-on-brand)", border: "none", borderRadius: "8px", padding: "7px 18px", fontWeight: 600 }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Student Question Analysis Modal (reuses same endpoint as ilearn /test-result) */}
      <Modal
        show={qaModal.show}
        onHide={() => setQaModal({ show: false, student: null, data: null })}
        size="xl"
        centered
        dialogClassName="qa-modal-dialog"
      >
        <style>{`
          /* ===== Premium QA Modal — world-class design system ===== */
          .qa-modal-dialog { max-width: 95vw !important; margin: 1rem auto !important; }
          .qa-modal-dialog * { box-sizing: border-box; }
          .qa-modal-dialog .modal-content {
            border: none; border-radius: 18px; overflow: hidden; width: 95vw;
            box-shadow: 0 30px 60px -20px rgba(15,23,42,0.35), 0 12px 24px -12px rgba(15,23,42,0.18);
            background: #fff;
          }
          .qa-modal-dialog .modal-header {
            background:
              radial-gradient(1000px 400px at 10% -60%, rgba(139,92,246,0.55), transparent 60%),
              radial-gradient(900px 400px at 90% 140%, rgba(14,165,233,0.5), transparent 60%),
              linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #0b1220 100%);
            border-bottom: none; padding: 18px 26px; position: relative;
          }
          .qa-modal-dialog .modal-header::after {
            content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 1px;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          }
          .qa-modal-dialog .modal-header .btn-close { filter: invert(1) brightness(2); opacity: 0.85; }
          .qa-modal-dialog .modal-body {
            background:
              radial-gradient(1200px 600px at -10% -10%, rgba(99,102,241,0.07), transparent 50%),
              radial-gradient(1200px 600px at 110% 110%, rgba(14,165,233,0.06), transparent 50%),
              #f8fafc;
            padding: 26px;
            max-height: 82vh;
            overflow-y: auto;
            font-feature-settings: "cv02", "cv03", "cv04", "cv11";
          }
          .qa-modal-dialog .modal-body::-webkit-scrollbar { width: 10px; height: 10px; }
          .qa-modal-dialog .modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; border: 2px solid #f8fafc; }
          .qa-modal-dialog .modal-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
          .qa-modal-dialog .modal-footer { background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 14px 26px; }

          /* Header avatar glow */
          .qa-avatar { position: relative; }
          .qa-avatar::before {
            content: ""; position: absolute; inset: -4px; border-radius: 12px;
            background: linear-gradient(135deg, #10b981, #22d3ee, #8b5cf6);
            opacity: 0.55; filter: blur(8px); z-index: -1;
          }

          /* ===== Summary cards ===== */
          .qa-summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; margin-bottom: 22px; }
          .qa-summary-card {
            background: #fff; border-radius: 14px; padding: 18px 20px;
            border: 1px solid #e2e8f0;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 8px 20px -12px rgba(15,23,42,0.12);
            position: relative; overflow: hidden;
          }
          .qa-summary-card::before {
            content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px;
            background: var(--gradient-brand);
          }
          .qa-summary-card.ok::before { background: linear-gradient(90deg, #10b981, #22c55e); }
          .qa-summary-card.warn::before { background: linear-gradient(90deg, #f59e0b, #ef4444); }
          .qa-summary-title { font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
          .qa-summary-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .qa-summary-stat { display: flex; flex-direction: column; gap: 2px; }
          .qa-summary-stat-label { font-size: 0.68rem; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
          .qa-summary-stat-value { font-size: 1.55rem; font-weight: 800; color: #0f172a; line-height: 1.1; font-variant-numeric: tabular-nums; letter-spacing: -0.02em; }
          .qa-summary-stat-value.good { color: #16a34a; }
          .qa-summary-stat-value.bad  { color: #dc2626; }
          .qa-summary-stat-value.muted { color: #64748b; }
          .qa-summary-stat-sub { font-size: 0.72rem; color: #94a3b8; font-weight: 500; }

          /* ===== Section card ===== */
          .qa-section-card {
            background: #fff; border: 1px solid #e2e8f0; border-radius: 16px;
            margin-bottom: 18px; overflow: hidden;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 10px 30px -18px rgba(15,23,42,0.2);
          }
          .qa-section-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 14px 18px;
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            border-bottom: 1px solid #e2e8f0;
          }
          .qa-section-name {
            display: flex; align-items: center; gap: 12px;
            font-size: 0.98rem; font-weight: 700; color: #0f172a; letter-spacing: -0.01em;
          }
          .qa-section-badge {
            width: 30px; height: 30px; border-radius: 9px;
            background: var(--gradient-brand);
            color: #fff; display: inline-flex; align-items: center; justify-content: center;
            font-size: 0.82rem; font-weight: 800;
            box-shadow: 0 4px 12px -4px rgba(99,102,241,0.5);
          }
          .qa-section-subtitle { font-size: 0.72rem; color: #94a3b8; font-weight: 500; margin-left: 4px; }
          .qa-stats-inline { display: flex; gap: 6px; }
          .qa-stat-pill {
            display: inline-flex; align-items: center; gap: 5px;
            padding: 4px 11px; border-radius: 999px;
            font-size: 0.72rem; font-weight: 700;
            border: 1px solid; font-variant-numeric: tabular-nums;
          }
          .qa-stat-pill.correct { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
          .qa-stat-pill.wrong   { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
          .qa-stat-pill.unatt   { background: #f8fafc; color: #475569; border-color: #e2e8f0; }

          /* ===== Question table ===== */
          .qa-question-table { width: 100%; border-collapse: collapse; }
          .qa-question-table thead th {
            background: #f8fafc; color: #475569;
            padding: 11px 14px;
            font-size: 0.68rem; font-weight: 700; text-align: center;
            text-transform: uppercase; letter-spacing: 0.6px;
            border-bottom: 1px solid #e2e8f0;
            position: sticky; top: 0; z-index: 1;
          }
          .qa-question-table thead th:first-child { text-align: center; }
          .qa-question-table tbody td {
            padding: 12px 14px;
            font-size: 0.86rem; color: #0f172a;
            border-bottom: 1px solid #f1f5f9;
            vertical-align: middle;
            transition: background 0.15s ease;
          }
          .qa-question-table tbody tr:last-child td { border-bottom: none; }
          .qa-question-table tbody tr { cursor: pointer; transition: background 0.15s ease; }
          .qa-question-table tbody tr:hover td { background: #f8fafc; }
          .qa-question-table tbody tr.qa-row-expanded td { background: #eef2ff; border-bottom-color: #c7d2fe; }

          /* Question number chip */
          .qa-qnum {
            display: inline-flex; align-items: center; gap: 6px;
            font-weight: 700; color: #0f172a; font-variant-numeric: tabular-nums;
          }
          .qa-qnum-chevron {
            color: #94a3b8; font-size: 0.72rem; transition: transform 0.2s ease;
            display: inline-block;
          }
          .qa-row-expanded .qa-qnum-chevron { transform: rotate(90deg); color: #4338ca; }

          /* Answer / clock cells */
          .qa-clock {
            font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
            font-size: 0.78rem; color: #475569;
            background: #f1f5f9; padding: 3px 9px; border-radius: 6px;
            display: inline-block; font-variant-numeric: tabular-nums;
          }
          .qa-answer-cell {
            text-align: center; font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
            font-weight: 700; font-size: 0.88rem; letter-spacing: 0.4px;
            color: #0f172a;
          }
          .qa-answer-cell.correct { color: #047857; }
          .qa-score-cell {
            text-align: center; font-weight: 800; font-size: 0.92rem;
            font-variant-numeric: tabular-nums;
          }
          .qa-unattempted { color: #cbd5e1; font-style: italic; font-size: 0.82rem; font-weight: 500; }

          /* Status pills — modern soft-fill with ring */
          .qa-status-pill {
            display: inline-flex; align-items: center; gap: 6px;
            padding: 5px 14px; border-radius: 999px;
            font-size: 0.72rem; font-weight: 700; letter-spacing: 0.3px;
            border: 1px solid; cursor: pointer; transition: all 0.2s ease;
            user-select: none;
          }
          .qa-status-pill::before {
            content: ""; width: 6px; height: 6px; border-radius: 50%;
          }
          .qa-status-pill.correct { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
          .qa-status-pill.correct::before { background: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.2); }
          .qa-status-pill.wrong   { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
          .qa-status-pill.wrong::before   { background: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.2); }
          .qa-status-pill.unatt   { background: #f8fafc; color: #475569; border-color: #e2e8f0; }
          .qa-status-pill.unatt::before   { background: #94a3b8; box-shadow: 0 0 0 3px rgba(148,163,184,0.2); }
          .qa-status-pill:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 10px -4px rgba(15,23,42,0.18);
          }

          /* Expanded detail panel */
          .qa-detail-panel {
            background: linear-gradient(180deg, #eef2ff 0%, #f8fafc 100%);
            border-left: 3px solid #6366f1;
            padding: 18px 22px;
            animation: qaFadeIn 0.25s ease;
          }
          @keyframes qaFadeIn {
            from { opacity: 0; transform: translateY(-4px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .qa-detail-title {
            font-size: 0.68rem; font-weight: 800; color: #4338ca;
            text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px;
            display: flex; align-items: center; gap: 8px;
          }
          .qa-detail-stem {
            font-size: 0.95rem; color: #0f172a; line-height: 1.65;
            margin-bottom: 14px; padding: 12px 14px;
            background: #fff; border-radius: 10px; border: 1px solid #e2e8f0;
          }
          .qa-option {
            padding: 10px 14px; border-radius: 10px; margin-bottom: 6px;
            font-size: 0.9rem; display: flex; align-items: center; gap: 10px;
            border: 1px solid #e2e8f0; background: #fff;
            transition: all 0.15s ease;
          }
          .qa-option .qa-option-letter {
            width: 28px; height: 28px; border-radius: 7px;
            background: #f1f5f9; color: #334155;
            display: inline-flex; align-items: center; justify-content: center;
            font-weight: 700; font-size: 0.78rem; flex-shrink: 0;
          }
          .qa-option.correct { background: #ecfdf5; border-color: #10b981; }
          .qa-option.correct .qa-option-letter { background: #10b981; color: #fff; }
          .qa-option.wrong-picked { background: #fef2f2; border-color: #ef4444; }
          .qa-option.wrong-picked .qa-option-letter { background: #ef4444; color: #fff; }
          .qa-option-tag {
            font-size: 0.68rem; font-weight: 700; padding: 3px 8px; border-radius: 999px;
            letter-spacing: 0.3px; flex-shrink: 0;
          }
          .qa-option-tag.correct { background: #10b981; color: #fff; }
          .qa-option-tag.wrong-picked { background: #ef4444; color: #fff; }
          .qa-option-tag.student-correct { background: #059669; color: #fff; }

          .qa-triple {
            display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 10px; margin-top: 14px;
          }
          .qa-mini {
            background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
            padding: 12px 14px;
          }
          .qa-mini-label {
            font-size: 0.66rem; font-weight: 800; color: #64748b;
            text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px;
          }
          .qa-mini-value {
            font-size: 1rem; font-weight: 700; font-family: ui-monospace, "SF Mono", Menlo, monospace;
            font-variant-numeric: tabular-nums;
          }
          .qa-mini-value.correct { color: #16a34a; }
          .qa-mini-value.wrong { color: #dc2626; }
          .qa-mini-value.muted { color: #94a3b8; }

          @media (max-width: 768px) {
            .qa-summary-row { grid-template-columns: repeat(3, 1fr); }
            .qa-modal-dialog { max-width: 100vw !important; margin: 0 !important; }
            .qa-modal-dialog .modal-content { width: 100vw; border-radius: 0; }
          }
        `}</style>
        <Modal.Header closeButton style={{ color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "0.98rem", fontWeight: 600, color: "var(--text-on-brand)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                className="qa-avatar"
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, var(--status-success) 0%, var(--status-success) 100%)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: "1rem",
                }}
              >
                Q
              </span>
              <div style={{ lineHeight: 1.25 }}>
                <div style={{ fontSize: "1.02rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{qaModal.student?.studentName || ""}</div>
                <div style={{ fontSize: "0.74rem", opacity: 0.75, fontWeight: 500, marginTop: "2px" }}>
                  {qaModal.data?.testName || data?.test?.name || ""} · Question Analysis
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="text-center py-4"><Spinner role="status" animation="border" size="sm" /> <span style={{ marginLeft: "8px", color: "var(--text-muted)" }}>Loading question analysis…</span></div>
          ) : qaModal.data?.error ? (
            <div style={{ padding: "12px 16px", background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: "10px", color: "var(--status-danger)" }}>
              {qaModal.data.error}
            </div>
          ) : qaModal.data?.directResult === false || qaModal.data?.instantResult === false ? (
            <div style={{ padding: "20px", background: "#fef9c3", border: "1px solid var(--status-warning-border)", borderRadius: "10px", color: "#854d0e", textAlign: "center" }}>
              Question-wise analysis is not available for this test type.
            </div>
          ) : qaModal.data?.sections?.length > 0 ? (
            <>
              {/* Result Hero + Section Breakdown — single visual block. The
                  hero shows the big score; the section table mirrors what the
                  iLearn /test-result page shows (Section / Marks / Max / %). */}
              {qaModal.data.summary && (() => {
                const summary = qaModal.data.summary;
                const marksObtained = Number(summary.marksObtained ?? 0);
                const totalMarks = Number(summary.totalMarks ?? 0);
                const percentage = totalMarks > 0 ? (marksObtained / totalMarks) * 100 : 0;
                const correct = Number(summary.correctQuestions ?? 0);
                const wrong = Number(summary.wrongQuestions ?? 0);
                const unattempted = Number(summary.unattemptedQuestions ?? 0);
                const totalQ = Number(summary.numberOfQuestions ?? (correct + wrong + unattempted));
                const attempted = totalQ - unattempted;
                // Sections list: read from data.testTableData if present,
                // else compute from data.sections. testTableData (from the
                // ilearn API) carries Marks/Max/% straight from the backend.
                const sectionRows: Array<{ name: string; marks: number; max: number; pct: number; isTotal?: boolean }> = [];
                if (Array.isArray(qaModal.data.testTableData) && qaModal.data.testTableData.length > 0) {
                  for (const r of qaModal.data.testTableData) {
                    const m = Number(r.marks ?? r.Marks ?? 0);
                    const max = Number(r.totalMarks ?? r.maxMarks ?? r.MaxMarks ?? 0);
                    sectionRows.push({
                      name: String(r.sectionName ?? r.SectionName ?? ""),
                      marks: m,
                      max,
                      pct: max > 0 ? (m / max) * 100 : 0,
                      isTotal: String(r.sectionName ?? "").toLowerCase().includes("total"),
                    });
                  }
                } else {
                  for (const s of qaModal.data.sections as any[]) {
                    const m = Number(s.marksObtained ?? 0);
                    const max = Number(s.totalMarks ?? 0);
                    sectionRows.push({
                      name: String(s.sectionName ?? ""),
                      marks: m,
                      max,
                      pct: max > 0 ? (m / max) * 100 : 0,
                    });
                  }
                  sectionRows.push({ name: "Test Total", marks: marksObtained, max: totalMarks, pct: percentage, isTotal: true });
                }
                // Color the hero ribbon by performance band.
                const heroGradient =
                  percentage >= 75 ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" :
                  percentage >= 50 ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" :
                  percentage >= 35 ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" :
                  "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)";

                return (
                  <div style={{ marginBottom: 16 }}>
                    {/* Hero: big score + percentage + four stat pills */}
                    <div style={{
                      background: heroGradient,
                      color: "var(--text-on-brand)",
                      borderRadius: 14,
                      padding: "18px 22px",
                      display: "grid",
                      gridTemplateColumns: "minmax(220px, 1.1fr) 2fr",
                      gap: 22,
                      alignItems: "center",
                      boxShadow: "0 10px 30px rgba(2, 6, 23, 0.15)",
                    }}>
                      <div>
                        <div style={{ fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.85, fontWeight: 600 }}>
                          Marks Obtained
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                          <span style={{ fontSize: "2.4rem", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1 }}>
                            {marksObtained}
                          </span>
                          <span style={{ fontSize: "1.1rem", fontWeight: 600, opacity: 0.85 }}>
                            / {totalMarks}
                          </span>
                        </div>
                        <div style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 10,
                          background: "rgba(255,255,255,0.18)",
                          padding: "5px 12px",
                          borderRadius: 999,
                          fontSize: "0.86rem",
                          fontWeight: 700,
                          backdropFilter: "blur(6px)",
                        }}>
                          {percentage.toFixed(2)}%
                        </div>
                      </div>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 10,
                      }}>
                        {[
                          { label: "Attempted", value: `${attempted}/${totalQ}`, accent: "rgba(255,255,255,0.22)" },
                          { label: "Correct", value: correct, accent: "rgba(16, 185, 129, 0.35)" },
                          { label: "Wrong", value: wrong, accent: "rgba(239, 68, 68, 0.38)" },
                          { label: "Unattempted", value: unattempted, accent: "rgba(255,255,255,0.18)" },
                        ].map((s) => (
                          <div key={s.label} style={{
                            background: s.accent,
                            borderRadius: 10,
                            padding: "10px 12px",
                            textAlign: "center",
                            border: "1px solid rgba(255,255,255,0.22)",
                          }}>
                            <div style={{ fontSize: "1.5rem", fontWeight: 800, lineHeight: 1.05 }}>{s.value}</div>
                            <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.85, marginTop: 4, fontWeight: 600 }}>
                              {s.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Section breakdown table with inline progress bars */}
                    <div style={{
                      marginTop: 14,
                      background: "var(--surface-card)",
                      borderRadius: 12,
                      border: "1px solid var(--border-default)",
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
                    }}>
                      <div style={{
                        background: "var(--surface-sunken)",
                        padding: "10px 18px",
                        borderBottom: "1px solid var(--border-default)",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--text-strong)",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}>
                        Section Breakdown
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                        <thead>
                          <tr style={{ background: "var(--surface-page)", color: "var(--text-body)" }}>
                            <th style={{ textAlign: "left", padding: "10px 18px", fontWeight: 600, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Section</th>
                            <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 600, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em", width: 90 }}>Marks</th>
                            <th style={{ textAlign: "right", padding: "10px 14px", fontWeight: 600, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em", width: 90 }}>Max</th>
                            <th style={{ textAlign: "left", padding: "10px 14px", fontWeight: 600, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em", width: 220 }}>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sectionRows.map((row, idx) => {
                            const barColor =
                              row.pct >= 75 ? "#10b981" :
                              row.pct >= 50 ? "#3b82f6" :
                              row.pct >= 35 ? "#f59e0b" : "#ef4444";
                            return (
                              <tr key={idx} style={{
                                borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
                                background: row.isTotal ? "#f8fafc" : "#fff",
                              }}>
                                <td style={{
                                  padding: "12px 18px",
                                  fontWeight: row.isTotal ? 700 : 500,
                                  color: row.isTotal ? "#0f172a" : "#334155",
                                }}>
                                  {row.name}
                                </td>
                                <td style={{ textAlign: "right", padding: "12px 14px", fontWeight: row.isTotal ? 700 : 500, color: "var(--text-strong)" }}>{row.marks}</td>
                                <td style={{ textAlign: "right", padding: "12px 14px", color: "var(--text-muted)", fontWeight: row.isTotal ? 700 : 500 }}>{row.max}</td>
                                <td style={{ padding: "12px 14px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <div style={{ flex: 1, height: 8, background: "var(--surface-sunken)", borderRadius: 6, overflow: "hidden" }}>
                                      <div style={{
                                        width: `${Math.min(100, Math.max(0, row.pct))}%`,
                                        height: "100%",
                                        background: barColor,
                                        transition: "width 0.3s ease",
                                      }} />
                                    </div>
                                    <span style={{
                                      minWidth: 56,
                                      textAlign: "right",
                                      fontWeight: row.isTotal ? 700 : 600,
                                      color: row.isTotal ? "#0f172a" : "#475569",
                                      fontSize: "0.86rem",
                                    }}>
                                      {row.pct.toFixed(2)}%
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Sections — premium white cards */}
              {qaModal.data.sections.map((sec: any, si: number) => {
                const correctCount = sec.questions.filter((q: any) => q.attemptStatus === 1).length;
                const wrongCount = sec.questions.filter((q: any) => q.attemptStatus === 0).length;
                const unattCount = sec.questions.filter((q: any) => q.attemptStatus === -1).length;
                return (
                  <div key={si} className="qa-section-card">
                    <div className="qa-section-header">
                      <div className="qa-section-name">
                        <span className="qa-section-badge">{si + 1}</span>
                        <span>{sec.sectionName}</span>
                        {qaModal.data.summary?.setName && (
                          <span className="qa-section-subtitle">/ {qaModal.data.summary.setName}</span>
                        )}
                      </div>
                      <div className="qa-stats-inline">
                        <span className="qa-stat-pill correct">&#10003; {correctCount}</span>
                        <span className="qa-stat-pill wrong">&#10007; {wrongCount}</span>
                        <span className="qa-stat-pill unatt">&ndash; {unattCount}</span>
                      </div>
                    </div>
                    <table className="qa-question-table">
                      <thead>
                        <tr>
                          <th style={{ width: "80px", textAlign: "left", paddingLeft: "18px" }}>Q#</th>
                          <th style={{ width: "130px" }}>Time</th>
                          <th>Your Response</th>
                          <th>Correct Answer</th>
                          <th style={{ width: "110px" }}>Score</th>
                          <th style={{ width: "130px" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sec.questions.map((q: any, qi: number) => {
                          const t = Number(q.attemptTime) || 0;
                          const min = Math.floor(t / 60);
                          const sec2 = t - min * 60;
                          const timeStr = min > 0 ? `${min} MIN ${sec2} SEC` : `${sec2} SEC`;
                          const qpQuestion = questionsById[q.questionId];
                          const correctFromChoices = qpQuestion?.choices
                            ? qpQuestion.choices
                                .filter((c: any) => Number(c.correctFlag) === 1)
                                .map((c: any) => c.choiceString)
                                .join(", ")
                            : "";
                          const correctAns =
                            q.questionCategory === 0
                              ? ""
                              : q.questionCategory === 5
                              ? `${q.answerRangeStart ?? ""}-${q.answerRangeEnd ?? ""}`
                              : q.correctString || correctFromChoices || q.textAnswer || "--";
                          const statusClass =
                            q.attemptStatus === 1 ? "green" : q.attemptStatus === -1 ? "blue" : "orange";
                          const statusLabel =
                            q.attemptStatus === 1 ? "Correct" : q.attemptStatus === -1 ? "Unattempted" : "Wrong";
                          const rowKey = `${si}-${qi}`;
                          const expanded = expandedRowKey === rowKey;
                          const studentAnswer = q.attemptedString || q.textAnswer || "";

                          const statusPillClass = q.attemptStatus === 1 ? "correct" : q.attemptStatus === -1 ? "unatt" : "wrong";
                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                onClick={() => setExpandedRowKey(expanded ? null : rowKey)}
                                className={expanded ? "qa-row-expanded" : ""}
                                title="Click to view question, correct answer and student's response"
                              >
                                <td style={{ textAlign: "left", paddingLeft: "18px" }}>
                                  <span className="qa-qnum">
                                    <span className="qa-qnum-chevron">&#9656;</span>
                                    Q{q.questionOrder || q.index}
                                  </span>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span className="qa-clock">{timeStr}</span>
                                </td>
                                <td className="qa-answer-cell">
                                  {q.attemptedString ? q.attemptedString
                                    : q.textAnswer ? <span dangerouslySetInnerHTML={{ __html: q.textAnswer }} />
                                    : <span className="qa-unattempted">—</span>}
                                </td>
                                <td className="qa-answer-cell correct">{correctAns || "—"}</td>
                                <td className="qa-score-cell" style={{ color: q.attemptStatus === 1 ? "#16a34a" : q.attemptStatus === -1 ? "#94a3b8" : "#dc2626" }}>
                                  {q.attemptStatus === -1 ? (
                                    <span className="qa-unattempted">—</span>
                                  ) : (
                                    <>{q.marks}<span style={{ color: "var(--text-faint)", fontWeight: 500 }}> / {parseFloat(q.actualMarks)}</span></>
                                  )}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span
                                    className={`qa-status-pill ${statusPillClass}`}
                                    onClick={(e) => { e.stopPropagation(); setExpandedRowKey(expanded ? null : rowKey); }}
                                  >
                                    {statusLabel}
                                  </span>
                                </td>
                              </tr>
                              {expanded && (
                                <tr>
                                  <td colSpan={6} style={{ padding: 0 }}>
                                    <div className="qa-detail-panel">
                                      {qpQuestion ? (
                                        <>
                                          <div className="qa-detail-title">
                                            <span>&#128196;</span>
                                            Question {q.questionOrder || q.index}
                                            {qpQuestion.marks != null && (
                                              <span style={{ marginLeft: "6px", color: "var(--text-muted)", fontWeight: 600, letterSpacing: 0 }}>
                                                · +{qpQuestion.marks}
                                                {qpQuestion.negativeMarks ? ` / -${Math.abs(qpQuestion.negativeMarks)}` : ""}
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            className="qa-detail-stem"
                                            dangerouslySetInnerHTML={{ __html: qpQuestion.stemStatement || qpQuestion.questionName || "" }}
                                          />
                                          {qpQuestion.choices?.length > 0 && (
                                            <>
                                              <div className="qa-detail-title">
                                                <span>&#128221;</span> Options
                                              </div>
                                              {qpQuestion.choices.map((c: any, ci: number) => {
                                                const isCorrect = Number(c.correctFlag) === 1;
                                                const studentPicked = studentAnswer && studentAnswer.split(",").map((s: string) => s.trim()).includes(c.choiceString);
                                                let optClass = "qa-option";
                                                let tag = null;
                                                if (isCorrect) {
                                                  optClass += " correct";
                                                  tag = <span className="qa-option-tag correct">&#10003; Correct</span>;
                                                }
                                                if (studentPicked && !isCorrect) {
                                                  optClass = "qa-option wrong-picked";
                                                  tag = <span className="qa-option-tag wrong-picked">&#10007; Student Picked</span>;
                                                }
                                                if (studentPicked && isCorrect) {
                                                  tag = <span className="qa-option-tag student-correct">&#9733; Student&apos;s Answer</span>;
                                                }
                                                return (
                                                  <div key={ci} className={optClass}>
                                                    <span className="qa-option-letter">{c.choiceString}</span>
                                                    <span style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: c.name || "" }} />
                                                    {tag}
                                                  </div>
                                                );
                                              })}
                                            </>
                                          )}
                                          <div className="qa-triple">
                                            <div className="qa-mini">
                                              <div className="qa-mini-label">Student&apos;s Answer</div>
                                              <div className={`qa-mini-value ${q.attemptStatus === 1 ? "correct" : q.attemptStatus === -1 ? "muted" : "wrong"}`}>
                                                {studentAnswer || "Unattempted"}
                                              </div>
                                            </div>
                                            <div className="qa-mini">
                                              <div className="qa-mini-label">Correct Answer</div>
                                              <div className="qa-mini-value correct">
                                                {correctAns || "—"}
                                              </div>
                                            </div>
                                            <div className="qa-mini">
                                              <div className="qa-mini-label">Score</div>
                                              <div className={`qa-mini-value ${q.attemptStatus === 1 ? "correct" : q.attemptStatus === -1 ? "muted" : "wrong"}`}>
                                                {q.attemptStatus === -1 ? "Unattempted" : `${q.marks} / ${parseFloat(q.actualMarks)}`}
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      ) : (
                                        <div style={{ color: "var(--text-muted)", fontSize: "0.85rem", fontStyle: "italic", padding: "8px 0" }}>
                                          Question text is not available — question paper JSON could not be loaded.
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ padding: "20px", background: "var(--surface-page)", border: "1px dashed var(--border-strong)", borderRadius: "10px", color: "var(--text-muted)", textAlign: "center" }}>
              No response data available for this student.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            onClick={() => setQaModal({ show: false, student: null, data: null })}
            style={{ backgroundColor: "#475569", color: "var(--text-on-brand)", border: "none", borderRadius: "8px", padding: "7px 18px", fontWeight: 600 }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Column Visibility Config Modal (tenant-wide; admin only) */}
      <Modal show={showColsModal} onHide={() => setShowColsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>
            Configure Result Columns
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: "12px" }}>
            Choose which columns appear on the Test Results screen. This setting is
            saved tenant-wide. <strong>Student Name</strong>, <strong>Code</strong>, and{" "}
            <strong>Marks</strong> are always shown.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 10px",
              marginBottom: "10px",
              background: "#f3f0fa",
              border: "1px solid #d8cef0",
              borderRadius: "6px",
            }}
          >
            <Form.Check
              type="checkbox"
              id="col-cfg-select-all"
              label={<strong>Check all</strong>}
              checked={CONFIGURABLE_COLUMNS.every((c) => colsDraft[c.key] === true)}
              ref={(el: HTMLInputElement | null) => {
                if (el) {
                  const checkedCount = CONFIGURABLE_COLUMNS.filter((c) => colsDraft[c.key] === true).length;
                  el.indeterminate = checkedCount > 0 && checkedCount < CONFIGURABLE_COLUMNS.length;
                }
              }}
              onChange={(e) => {
                const checked = e.target.checked;
                const next: Record<string, boolean> = {};
                for (const c of CONFIGURABLE_COLUMNS) next[c.key] = checked;
                setColsDraft(next);
              }}
              style={{ fontSize: "0.85rem", marginBottom: 0 }}
            />
            <span style={{ fontSize: "0.75rem", color: "#6f42c1", fontWeight: 600 }}>
              {CONFIGURABLE_COLUMNS.filter((c) => colsDraft[c.key] === true).length} / {CONFIGURABLE_COLUMNS.length} selected
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
            {CONFIGURABLE_COLUMNS.map((c) => (
              <Form.Check
                key={c.key}
                type="checkbox"
                id={`col-cfg-${c.key}`}
                label={colLabel(c.key)}
                checked={colsDraft[c.key] === true}
                onChange={(e) =>
                  setColsDraft((prev) => ({ ...prev, [c.key]: e.target.checked }))
                }
                style={{ fontSize: "0.85rem" }}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowColsModal(false)}
            disabled={savingCols}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", borderRadius: "6px", padding: "6px 16px", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={saveColsConfig}
            disabled={savingCols}
            style={{ backgroundColor: "#6f42c1", color: "var(--text-on-brand)", border: "none", borderRadius: "6px", padding: "6px 16px", fontWeight: 600 }}
          >
            {savingCols ? <Spinner role="status" animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TestResultsPanel;
